// frontend/packages/business-console/src/hooks/useAutosave.js
//
// Debounced autosave hook. Saves content snapshots to Azure Blob Storage
// via the versioning API. Does NOT create git commits — that only happens
// when the user explicitly clicks Save Draft.
//
// Usage in ContentEditor:
//   useAutosave({ filePath, content: combinedCurrent, enabled: mode === "edit" });

import { useEffect, useRef, useState } from "react";
import { autosaveContent } from "../services/contentVersionApi";

const DEBOUNCE_MS = 30000; // 30 seconds

export default function useAutosave({ filePath, content, enabled = true }) {
  const [lastSaved, setLastSaved] = useState(null);
  const [autosaveError, setAutosaveError] = useState(null);
  const timerRef = useRef(null);
  const lastContentRef = useRef(content);
  const lastSavedContentRef = useRef(null);

  useEffect(() => {
    lastContentRef.current = content;
  }, [content]);

  useEffect(() => {
    if (!enabled || !filePath) return;

    if (timerRef.current) clearTimeout(timerRef.current);

    timerRef.current = setTimeout(async () => {
      const current = lastContentRef.current;
      if (!current || current === lastSavedContentRef.current) return;

      try {
        await autosaveContent(filePath, current);
        lastSavedContentRef.current = current;
        setLastSaved(new Date().toISOString());
        setAutosaveError(null);
      } catch (e) {
        setAutosaveError(e?.message || "Autosave failed");
      }
    }, DEBOUNCE_MS);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [content, enabled, filePath]);

  return { lastSaved, autosaveError };
}
